import { twinService } from '../../services/twinService';
import { EMPTY_TOKEN_VALUES, resolveEntityTitle } from '../farm/displayName';

/**
 * What a match is called when the farm view's title chain finds nothing: the
 * attributes the lens titles name objects by that the generic chain lacks — an
 * account by its bank, an invoice by its number, a seed lot by its variety, and
 * last an operation by its type.
 */
const EXTRA_NAME_KEYS: readonly string[] = [
  'bank_name',
  'invoice_number',
  'variety_name',
  'product_name',
  'plan_type',
  'pay_period',
  'purpose',
  'type',
];

/**
 * Pages read per search, at most (HOF-598). The server's `q` also matches key
 * names and ids, so one page can be all false positives; reading on is bounded
 * so a common term never walks the whole type.
 */
const MAX_PAGES = 5;

/**
 * The farm view's chain first (`resolveEntityTitle`: name → identifying keys →
 * the person's `first_name + last_name`), so the link form calls an object
 * what the farm view calls it (HOF-598); then the link-form extras. Empty when
 * nothing names the row — its fallback title is its id, which means nothing to
 * a farmer.
 */
function matchName(row: Record<string, unknown>): string {
  const { title, isFallback } = resolveEntityTitle({
    id: String(row.entity_id ?? ''),
    name: typeof row.name === 'string' ? row.name : undefined,
    properties: row,
  });
  if (!isFallback) return title;
  for (const key of EXTRA_NAME_KEYS) {
    const value = row[key];
    if (typeof value === 'string' && value.trim() && !EMPTY_TOKEN_VALUES.has(value.trim())) return value.trim();
    if (typeof value === 'number') return String(value);
  }
  return '';
}

/** The row's attribute VALUES (not the keys, not the id), lower-cased. */
function searchableValues(row: Record<string, unknown>): string[] {
  return Object.entries(row)
    .filter(([key, value]) => key !== 'entity_id' && (typeof value === 'string' || typeof value === 'number'))
    .map(([, value]) => String(value).toLocaleLowerCase());
}

/**
 * Whether some attribute VALUE contains `term` — or, for a term of several
 * words, every word is in some value: a person is offered as „Vorname
 * Nachname", and no single attribute holds that phrase (HOF-598).
 */
function valueMatches(row: Record<string, unknown>, term: string, words: readonly string[]): boolean {
  const values = searchableValues(row);
  const needle = term.toLocaleLowerCase();
  if (values.some((value) => value.includes(needle))) return true;
  return (
    words.length > 1 &&
    words.every((word) => values.some((value) => value.includes(word.toLocaleLowerCase())))
  );
}

/**
 * Twin objects of one type matching `q`, as `{entity_id, name}` — the link
 * form's object search ("Verknüpfung hinzufügen").
 *
 * It searches every attribute (the entity query's `q`), not the autocomplete
 * endpoint the fact editor uses: that one prefix-matches a `name` only, so a
 * parcel (named by its parcel number) or an account (named by its bank) could
 * never be found. The server's `q` also matches JSON key names and ids, so the
 * rows are kept only when a VALUE contains the term, and a row with no
 * identifier is dropped — it could only be offered by its id, which means
 * nothing to a farmer.
 *
 * The filter runs per page, and the search reads on until `limit` matches are
 * in, the server runs out, or `MAX_PAGES` are read (HOF-598): cutting to one
 * page first let key/id false positives fill it, and the form said "no match"
 * while real matches sat on the next page.
 */
export async function fetchLinkTargetSuggestions(entityType: string, q: string, limit = 8) {
  const term = q.trim();
  const words = term.split(/\s+/).filter(Boolean);
  // The server matches its `q` as one substring, so a phrase goes out as its
  // longest word — the most selective — and the words are checked here.
  const serverTerm = words.length > 1 ? words.reduce((a, b) => (b.length > a.length ? b : a)) : term;
  const pageSize = limit * 2;
  const matches: { entity_id: string; name: string }[] = [];
  const seen = new Set<string>();
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const result = await twinService.queryEntities(entityType, { q: serverTerm, limit: pageSize, page });
    for (const row of result.entities) {
      if (!valueMatches(row, term, words)) continue;
      const match = { entity_id: String(row.entity_id ?? ''), name: matchName(row) };
      if (!match.entity_id || !match.name || seen.has(match.entity_id)) continue;
      seen.add(match.entity_id);
      matches.push(match);
      if (matches.length >= limit) return matches;
    }
    const exhausted =
      result.has_more === false || (result.has_more === undefined && result.entities.length < pageSize);
    if (exhausted || result.entities.length === 0) break;
  }
  return matches;
}
